'use client';

import Image from 'next/image';

import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/**
 * Tiles for the light /customer-stories wall.
 *
 * The wall this replaces was near-black and did its talking in type: every
 * cell carried a headline, a caption, a CTA and up to two numbers, and the
 * photograph — where there was one — sat behind a scrim so the words could
 * be read over it. Twenty-three of those is a page you read rather than a
 * page you scan, and the photography we pay for was the thing losing.
 *
 * Here the picture is the card. A tile carries the customer's mark and, on
 * the wide ones, a single number. The headline is real and indexed but it is
 * `sr-only`: a reader recognises the logo or they don't, and if they don't,
 * a 14px line of "How X did Y with AI" was never going to be what convinced
 * them. It is the trade that buys the wall its quiet.
 *
 * Two shapes only — a square and a 92:45 wide — on a twelve-column grid.
 * Rhythm comes from where the wide ones fall, never from a third shape.
 */

export interface TileStat {
  value: string;
  label: string;
}

interface StoryTileProps {
  href: string;
  /** Indexed and announced; never painted. See the note above. */
  title: string;
  company: string;
  /** Customer wordmark from /public/logos. */
  wordmark?: string;
  /**
   * The mark is a coloured shape with a white glyph cut out of it. Inverting
   * one of those turns the shape and the glyph the same white and the tile
   * renders a blank rectangle — Banqy, Nature House, Rewaa and Syarah all
   * have it. These keep their own colours and take a stronger scrim instead.
   */
  wordmarkKeepsColor?: boolean;
  /**
   * `false` when a `keepsColor` mark is already drawn to sit on a photograph
   * — a reversed lockup, say — and so needs no plate behind it. The plate is
   * for marks whose own ink is too dark to read on a picture.
   */
  wordmarkPlate?: boolean;
  /**
   * The asset is an icon on a square canvas rather than a wordmark on a wide
   * one — Snel Een Taxi ships a 200x200 speech bubble. Fitted into the wide
   * box every other mark uses it comes out 52px across and reads as a stray
   * favicon; it gets a square box at the same optical weight instead.
   */
  markShape?: 'wide' | 'square';
  /**
   * Per-mark optical correction, around 1. See the note on `markScale` in the
   * index page for how it is derived and why a shared box is not enough.
   */
  markScale?: number;
  image?: string;
  /** Framing override for a subject that a centred crop would cut badly. */
  imagePosition?: string;
  /** Wide tiles only. A square tile carries the mark and nothing else. */
  stat?: TileStat;
  wide?: boolean;
  isRTL: boolean;
  className?: string;
}

/**
 * A white mark on a photograph is only legible if the pixels under it are
 * dark, and half our library is daylight. Sierra solves this with a radial
 * black blur under the mark rather than a scrim across the whole frame, and
 * it is the right answer: a bar across the middle of a photo destroys it,
 * while a soft pool at the centre reads as depth of field.
 */
const LogoPool = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute top-1/2 left-1/2 z-0 h-[85%] w-[85%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl"
    style={{
      // Deep enough to carry a white mark over a noon sky — the OneCar Now
      // and TicketSwap frames are both bright right where the mark lands, and
      // at 0.72 the mark was legible on the dark half of the library only.
      background:
        'radial-gradient(closest-side, rgba(0,0,0,0.88), rgba(0,0,0,0.34) 62%, rgba(0,0,0,0))',
    }}
  />
);

const TileMark = ({
  wordmark,
  company,
  keepsColor,
  plate = true,
  shape = 'wide',
  scale = 1,
  onPhoto,
}: {
  wordmark?: string;
  company: string;
  keepsColor?: boolean;
  plate?: boolean;
  shape?: 'wide' | 'square';
  scale?: number;
  onPhoto: boolean;
}) =>
  wordmark ? (
    <span
      className={cn(
        'relative z-10 flex items-center justify-center',
        // Same optical weight, two canvases: a wordmark gets a wide box, an
        // icon on a square canvas gets a square one. Fitting a 200x200 mark
        // into the wide box left it 52px across with 150px of dead air on
        // either side, which reads as a favicon that wandered in.
        // The square box runs larger than the wordmark box on purpose. Snel
        // Een Taxi's mark is drawn inside the middle half of a 200x200
        // viewBox, so a quarter of the box on every side is dead margin baked
        // into the asset and the bubble came out around 36px against the 52px
        // the wordmarks get. The asset is shared with five other surfaces —
        // including a marquee that sizes it against real wordmarks — so the
        // correction belongs here rather than in the file.
        shape === 'square'
          ? 'size-[88px] md:size-[112px]'
          : 'h-11 w-[10.5rem] md:h-[52px] md:w-[13rem]',
        // Four marks — Banqy, Nature House, Rewaa, Syarah — are a coloured
        // shape with the glyph cut out of it, so they cannot be knocked out
        // to white: shape and glyph go the same colour and the tile renders a
        // blank rectangle. They keep their own ink and get a white plate to
        // sit on, which is the only thing that holds a dark-green mark over a
        // photograph of a greenhouse. On the sand field they need nothing.
        onPhoto &&
          keepsColor &&
          plate &&
          (shape === 'square'
            ? 'rounded-2xl bg-white p-3 shadow-sm'
            : 'rounded-2xl bg-white px-7 py-5 shadow-sm'),
      )}
    >
      <img
        src={wordmark}
        alt={company}
        loading="lazy"
        // Scaled about its own centre rather than resized, so the shared box
        // still governs position and only the optical weight is corrected.
        style={scale === 1 ? undefined : { transform: `scale(${scale})` }}
        className={cn(
          // A fixed box, not an intrinsic size.
          //
          // Most of these wordmarks are viewBox-only SVGs with no width or
          // height attributes — OTO, Banqy, Nature House, Prêts and half a
          // dozen more — and an <img> of one of those reports a natural width
          // of zero. Any rule that asks the asset how wide it is (`w-auto`,
          // `max-h` with auto width) therefore renders it at zero by zero:
          // present in the DOM, invisible on the page, and silent about it.
          //
          // So the mark is given a box and `object-contain` fits it inside,
          // centred, whatever the asset declares. It also lands the thing we
          // wanted anyway: every mark occupies the same rectangle, so optical
          // size is a property of the wall rather than of whichever export we
          // happen to have of a given customer's logo.
          'block size-full object-contain',
          onPhoto && !keepsColor && 'brightness-0 invert',
        )}
      />
    </span>
  ) : (
    // No logo file yet. The name stands in, in the same place and at the same
    // optical weight, so a mark arriving later changes nothing but the asset.
    <span
      className={cn(
        'relative z-10 font-serif text-2xl leading-none md:text-3xl',
        onPhoto ? 'text-white' : 'text-foreground/70',
      )}
    >
      {company}
    </span>
  );

export const StoryTile = ({
  href,
  title,
  company,
  wordmark,
  wordmarkKeepsColor,
  wordmarkPlate,
  markShape,
  markScale,
  image,
  imagePosition,
  stat,
  wide,
  isRTL,
  className,
}: StoryTileProps) => (
  <Link
    href={href}
    prefetch={true}
    className={cn(
      'group col-span-12 block rounded-2xl outline-hidden',
      'focus-visible:ring-foreground focus-visible:ring-2 focus-visible:ring-offset-4',
      wide ? 'md:col-span-12 xl:col-span-8' : 'md:col-span-6 xl:col-span-4',
      className,
    )}
  >
    <figure
      className={cn(
        'bg-sand-100 relative overflow-hidden rounded-2xl',
        // The wide tile is a square until there is room for it to be wide;
        // a 92:45 letterbox on a phone is a strip, not a picture.
        wide ? 'aspect-square xl:aspect-[92/45]' : 'aspect-square',
      )}
    >
      {image ? (
        <Image
          src={image}
          alt=""
          fill
          // A square tile is a third of the grid and a wide one two thirds;
          // below xl both are full width. Without this every tile asks for a
          // full-viewport render and a wall of twenty-three pays for it.
          sizes={
            wide
              ? '(min-width: 1280px) 736px, 100vw'
              : '(min-width: 1280px) 360px, (min-width: 768px) 50vw, 100vw'
          }
          style={{ objectPosition: imagePosition }}
          className={cn(
            'object-cover',
            // The whole motion budget. A wall of twenty-three tiles that all
            // move is a screensaver; one that moves under the cursor is a
            // card that knows it was pointed at.
            'motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out',
            'motion-safe:group-hover:scale-[1.03]',
          )}
        />
      ) : null}

      {/* A photograph gets the pool; the sand placeholder needs nothing. */}
      {image ? <LogoPool /> : null}

      <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
        <TileMark
          wordmark={wordmark}
          company={company}
          keepsColor={wordmarkKeepsColor}
          plate={wordmarkPlate}
          shape={markShape}
          scale={markScale}
          onPhoto={Boolean(image)}
        />
      </div>

      {/* One number, on the floor of the tile, wide tiles only. The label
          sits above the figure rather than below it: read top-down the eye
          meets the subject before the quantity, which is the order the
          sentence would have been in. */}
      {stat && wide ? (
        <div
          className={cn(
            'absolute inset-x-0 bottom-0 z-10 flex flex-col gap-1 p-4 md:p-6',
            isRTL ? 'items-end text-right' : 'items-start text-left',
          )}
        >
          {/* White over a photograph, ink over the sand field a tile falls
              back to with no photograph yet — otherwise the number on a
              placeholder is white on near-white and simply gone. */}
          <span
            className={cn(
              'text-sm leading-5',
              image ? 'text-white/75' : 'text-muted-foreground',
            )}
          >
            {stat.label}
          </span>
          <span
            className={cn(
              'font-serif text-[38px] leading-none tabular-nums',
              image ? 'text-white' : 'text-foreground',
            )}
          >
            {stat.value}
          </span>
        </div>
      ) : null}

      {/* A scrim only where a number lands, and only as tall as the number. */}
      {stat && wide && image ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent"
        />
      ) : null}
    </figure>

    {/* The whole of the card's language, for a screen reader and for search.
        Nothing about this tile is decorative — the picture just isn't how a
        machine is going to read it. */}
    <span className="sr-only">{title}</span>
  </Link>
);
